import { supabase } from '@/lib/supabase';

interface QueryExpansion {
  originalQuery: string;
  expandedQueries: string[];
  synonyms: string[];
  relatedTerms: string[];
}

interface LanguageExpansionRules {
  [key: string]: {
    synonyms: { [key: string]: string[] };
    commonTerms: string[];
  };
}

// Enhanced language-specific expansion rules with Thai, Korean, Japanese
const expansionRules: LanguageExpansionRules = {
  en: {
    synonyms: {
      'bot': ['robot', 'automation', 'algorithm', 'trading bot', 'strategy'],
      'trading': ['trade', 'transaction', 'exchange', 'market', 'investment'],
      'profit': ['earnings', 'revenue', 'income', 'gains', 'return'],
      'risk': ['danger', 'threat', 'exposure', 'vulnerability', 'hazard'],
      'strategy': ['plan', 'approach', 'method', 'technique', 'tactic'],
      'account': ['profile', 'wallet', 'portfolio', 'balance'],
      'connect': ['link', 'attach', 'integrate', 'bind', 'join'],
      'platform': ['system', 'service', 'application', 'interface']
    },
    commonTerms: ['how to', 'what is', 'why', 'when', 'where', 'help', 'guide']
  },
  vi: {
    synonyms: {
      'bot': ['robot', 'tự động', 'thuật toán', 'bot giao dịch', 'chiến lược'],
      'giao dịch': ['trade', 'trao đổi', 'thị trường', 'đầu tư', 'mua bán'],
      'lợi nhuận': ['thu nhập', 'doanh thu', 'lãi', 'sinh lời'],
      'rủi ro': ['nguy hiểm', 'mối đe dọa', 'tổn thất', 'rủi ro'],
      'chiến lược': ['kế hoạch', 'phương pháp', 'cách thức', 'tactic'],
      'tài khoản': ['hồ sơ', 'ví', 'danh mục', 'số dư'],
      'kết nối': ['liên kết', 'tích hợp', 'ghép nối', 'kết hợp'],
      'nền tảng': ['hệ thống', 'dịch vụ', 'ứng dụng', 'giao diện']
    },
    commonTerms: ['làm thế nào', 'là gì', 'tại sao', 'khi nào', 'ở đâu', 'giúp', 'hướng dẫn']
  },
  zh: {
    synonyms: {
      '机器人': ['自动化', '算法', '交易机器人', '策略'],
      '交易': ['贸易', '交换', '市场', '投资', '买卖'],
      '利润': ['收入', '收益', '盈利', '回报'],
      '风险': ['危险', '威胁', '损失', '风险'],
      '策略': ['计划', '方法', '技术', '战术'],
      '账户': ['配置文件', '钱包', '投资组合', '余额'],
      '连接': ['链接', '集成', '绑定', '结合'],
      '平台': ['系统', '服务', '应用', '界面']
    },
    commonTerms: ['如何', '是什么', '为什么', '何时', '哪里', '帮助', '指南']
  },
  th: {
    synonyms: {
      'บอท': ['หุ่นยนต์', 'อัตโนมัติ', 'อัลกอริทึม', 'บอทเทรด', 'กลยุทธ์'],
      'การซื้อขาย': ['เทรด', 'แลกเปลี่ยน', 'ตลาด', 'การลงทุน', 'ซื้อขาย'],
      'กำไร': ['รายได้', 'ผลตอบแทน', 'เงินได้', 'ผลกำไร'],
      'ความเสี่ยง': ['อันตราย', 'ภัยคุกคาม', 'ความสูญเสีย', 'ความเสี่ยง'],
      'กลยุทธ์': ['แผน', 'วิธีการ', 'เทคนิค', 'ยุทธวิธี'],
      'บัญชี': ['โปรไฟล์', 'กระเป๋าเงิน', 'พอร์ตโฟลิโอ', 'ยอดคงเหลือ'],
      'เชื่อมต่อ': ['ลิงก์', 'รวม', 'ผูก', 'เข้าร่วม'],
      'แพลตฟอร์ม': ['ระบบ', 'บริการ', 'แอปพลิเคชัน', 'อินเทอร์เฟซ']
    },
    commonTerms: ['วิธีการ', 'คืออะไร', 'ทำไม', 'เมื่อไหร่', 'ที่ไหน', 'ช่วย', 'คู่มือ']
  },
  ko: {
    synonyms: {
      '봇': ['로봇', '자동화', '알고리즘', '트레이딩 봇', '전략'],
      '거래': ['트레이드', '교환', '시장', '투자', '매매'],
      '수익': ['소득', '수입', '이익', '수익률'],
      '위험': ['위험', '위협', '손실', '리스크'],
      '전략': ['계획', '방법', '기법', '전술'],
      '계정': ['프로필', '지갑', '포트폴리오', '잔액'],
      '연결': ['링크', '통합', '바인드', '결합'],
      '플랫폼': ['시스템', '서비스', '애플리케이션', '인터페이스']
    },
    commonTerms: ['어떻게', '무엇인가', '왜', '언제', '어디서', '도움', '가이드']
  },
  ja: {
    synonyms: {
      'ボット': ['ロボット', '自動化', 'アルゴリズム', 'トレーディングボット', '戦略'],
      '取引': ['トレード', '交換', '市場', '投資', '売買'],
      '利益': ['収入', '収益', '利得', 'リターン'],
      'リスク': ['危険', '脅威', '損失', 'リスク'],
      '戦略': ['計画', '方法', '技術', '戦術'],
      'アカウント': ['プロファイル', 'ウォレット', 'ポートフォリオ', '残高'],
      '接続': ['リンク', '統合', 'バインド', '結合'],
      'プラットフォーム': ['システム', 'サービス', 'アプリケーション', 'インターフェース']
    },
    commonTerms: ['どのように', '何ですか', 'なぜ', 'いつ', 'どこで', 'ヘルプ', 'ガイド']
  }
};

/**
 * Enhanced query expansion with improved multi-language support
 */
export async function expandQuery(
  originalQuery: string, 
  language: string = 'en',
  maxExpansions: number = 3
): Promise<QueryExpansion> {
  console.log('[QUERY EXPANSION] Enhanced expansion for query:', originalQuery, 'Language:', language);
  
  const result: QueryExpansion = {
    originalQuery,
    expandedQueries: [],
    synonyms: [],
    relatedTerms: []
  };
  
  try {
    // Get language-specific rules with fallback to English
    const rules = expansionRules[language] || expansionRules['en'];
    
    // Extract synonyms for terms in the query
    const queryLower = originalQuery.toLowerCase();
    const foundSynonyms: string[] = [];
    
    Object.entries(rules.synonyms).forEach(([term, synonyms]) => {
      if (queryLower.includes(term.toLowerCase())) {
        foundSynonyms.push(...synonyms.slice(0, 2)); // Limit synonyms per term
      }
    });
    
    result.synonyms = [...new Set(foundSynonyms)];
    
    // Create expanded queries by replacing terms with synonyms
    const expansions: string[] = [];
    
    // Add queries with synonym replacements
    result.synonyms.slice(0, maxExpansions).forEach(synonym => {
      let expandedQuery = originalQuery;
      Object.entries(rules.synonyms).forEach(([term, termSynonyms]) => {
        if (queryLower.includes(term.toLowerCase()) && termSynonyms.includes(synonym)) {
          expandedQuery = expandedQuery.replace(new RegExp(term, 'gi'), synonym);
        }
      });
      
      if (expandedQuery !== originalQuery && !expansions.includes(expandedQuery)) {
        expansions.push(expandedQuery);
      }
    });
    
    // Add related term combinations for better context
    const relatedTerms = rules.commonTerms.filter(term => 
      !queryLower.includes(term.toLowerCase())
    ).slice(0, 2);
    
    relatedTerms.forEach(term => {
      const expandedQuery = `${term} ${originalQuery}`;
      if (!expansions.includes(expandedQuery) && expansions.length < maxExpansions) {
        expansions.push(expandedQuery);
      }
    });
    
    result.expandedQueries = expansions.slice(0, maxExpansions);
    result.relatedTerms = relatedTerms;
    
    console.log('[QUERY EXPANSION] Enhanced result:', {
      original: originalQuery,
      expanded: result.expandedQueries.length,
      synonyms: result.synonyms.length,
      language: language
    });
    
    return result;
  } catch (error) {
    console.error('[QUERY EXPANSION] Error expanding query:', error);
    return result;
  }
}

/**
 * Enhanced query expansion using AI (fallback method)
 */
export async function expandQueryWithAI(
  originalQuery: string,
  language: string = 'en',
  topic: string = 'trading'
): Promise<string[]> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        messages: [
          {
            role: 'system',
            content: `Generate 2-3 alternative phrasings for search queries about ${topic}. Return only the alternative queries, one per line, without numbering or explanations.`
          },
          {
            role: 'user',
            content: `Original query: "${originalQuery}". Language: ${language}`
          }
        ],
        model: 'gpt-4o-mini',
        max_tokens: 150,
        temperature: 0.3
      }
    });
    
    if (error || !data?.content) {
      return [];
    }
    
    const alternatives = data.content
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line.length > 0 && line !== originalQuery)
      .slice(0, 3);
    
    console.log('[AI QUERY EXPANSION] Generated alternatives:', alternatives);
    return alternatives;
  } catch (error) {
    console.error('[AI QUERY EXPANSION] Error:', error);
    return [];
  }
}

/**
 * Combine results from multiple query variations with weighted scoring
 */
export function combineExpandedResults(
  originalResults: any[],
  expandedResults: any[][],
  originalWeight: number = 1.0,
  expandedWeight: number = 0.7
): any[] {
  const combinedMap = new Map();
  
  // Add original results with full weight
  originalResults.forEach(result => {
    combinedMap.set(result.id, {
      ...result,
      similarity: result.similarity * originalWeight,
      source: 'original'
    });
  });
  
  // Add expanded results with reduced weight
  expandedResults.forEach(results => {
    results.forEach(result => {
      const existing = combinedMap.get(result.id);
      if (existing) {
        // Take the higher similarity if document appears in multiple queries
        if (result.similarity * expandedWeight > existing.similarity) {
          combinedMap.set(result.id, {
            ...result,
            similarity: result.similarity * expandedWeight,
            source: 'expanded'
          });
        }
      } else {
        combinedMap.set(result.id, {
          ...result,
          similarity: result.similarity * expandedWeight,
          source: 'expanded'
        });
      }
    });
  });
  
  // Convert back to array and sort by similarity
  return Array.from(combinedMap.values())
    .sort((a, b) => b.similarity - a.similarity);
}
